import React from 'react';
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Stack,
  Paper,
  Typography,
  Box,
  Button,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

/*
  ConditionGroupsEditor: the rule-style "IF / OR IF" condition builder,
  extracted from the Rules form so anything that matches transactions can
  reuse it (rules, transaction-tracked goals). Controlled component:

   - groups: [{ conditions: [{ field, operator, value }] }]
   - onChange(nextGroups)
   - availableFields: [{ name, label, type }] from getTransactionFields()
*/
const ConditionGroupsEditor = ({ groups, onChange, availableFields = [] }) => {
  const handleConditionChange = (groupIndex, conditionIndex, key, val) => {
    const updated = groups.map((g) => ({ conditions: [...g.conditions] }));
    if (key === 'field') {
      const info = availableFields.find((f) => f.name === val);
      updated[groupIndex].conditions[conditionIndex] = {
        field: val,
        operator: info?.type === 'number' ? 'equals' : 'contains',
        value: '',
      };
    } else {
      updated[groupIndex].conditions[conditionIndex] = {
        ...updated[groupIndex].conditions[conditionIndex],
        [key]: val,
      };
    }
    onChange(updated);
  };

  const addCondition = (groupIndex) => {
    const updated = groups.map((g) => ({ conditions: [...g.conditions] }));
    updated[groupIndex].conditions.push({ field: 'description', operator: 'contains', value: '' });
    onChange(updated);
  };

  const removeCondition = (groupIndex, conditionIndex) => {
    if (groups[groupIndex].conditions.length <= 1) return; // keep at least one per group
    const updated = groups.map((g) => ({ conditions: [...g.conditions] }));
    updated[groupIndex].conditions.splice(conditionIndex, 1);
    onChange(updated);
  };

  const addConditionGroup = () => {
    onChange([
      ...groups,
      { conditions: [{ field: 'description', operator: 'contains', value: '' }] },
    ]);
  };

  const removeConditionGroup = (groupIndex) => {
    if (groups.length <= 1) return;
    const updated = [...groups];
    updated.splice(groupIndex, 1);
    onChange(updated);
  };

  return (
    <>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Conditions in a group use AND; separate groups use OR.
      </Typography>
      {groups.map((group, groupIndex) => (
        <Paper
          key={groupIndex}
          elevation={0}
          sx={{
            p: 1.5,
            mb: 1.5,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'grey.50'
          }}
        >
          {/* Group header — logic chip on the left, remove on the right */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 1 }}>
            <Chip
              label={groupIndex === 0 ? 'IF' : 'OR IF'}
              size="small"
              sx={{ backgroundColor: groupIndex === 0 ? 'primary.main' : 'warning.main', color: '#fff', fontWeight: 'bold' }}
            />
            {groups.length > 1 && (
              <IconButton size="small" color="error" onClick={() => removeConditionGroup(groupIndex)} aria-label="Remove group">
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          {/* Conditions */}
          <Stack spacing={1.5}>
            {group.conditions.map((condition, conditionIndex) => (
              <Box key={conditionIndex}>
                {conditionIndex > 0 && (
                  <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 700, display: 'block', mb: 0.5 }}>
                    AND
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <FormControl sx={{ flex: 1, minWidth: 0 }} size="small">
                    <InputLabel>Field</InputLabel>
                    <Select
                      value={condition.field}
                      onChange={e => handleConditionChange(groupIndex, conditionIndex, 'field', e.target.value)}
                      label="Field"
                    >
                      <MenuItem value="description" sx={{ fontSize: '0.875rem' }}>Description</MenuItem>
                      {availableFields.map(f =>
                        f.name !== 'description' ? (
                          <MenuItem key={f.name} value={f.name} sx={{ fontSize: '0.875rem' }}>
                            {f.label}
                          </MenuItem>
                        ) : null
                      )}
                    </Select>
                  </FormControl>

                  <FormControl sx={{ flex: 1, minWidth: 0 }} size="small">
                    <InputLabel>Operator</InputLabel>
                    <Select
                      value={condition.operator}
                      onChange={e => handleConditionChange(groupIndex, conditionIndex, 'operator', e.target.value)}
                      label="Operator"
                    >
                      <MenuItem value="contains" sx={{ fontSize: '0.875rem' }}>Contains</MenuItem>
                      <MenuItem value="equals" sx={{ fontSize: '0.875rem' }}>Equals</MenuItem>
                      <MenuItem value="startsWith" sx={{ fontSize: '0.875rem' }}>Starts with</MenuItem>
                      <MenuItem value="endsWith" sx={{ fontSize: '0.875rem' }}>Ends with</MenuItem>
                      <MenuItem value="greaterThan" sx={{ fontSize: '0.875rem' }}>Greater than</MenuItem>
                      <MenuItem value="lessThan" sx={{ fontSize: '0.875rem' }}>Less than</MenuItem>
                      <MenuItem value="between" sx={{ fontSize: '0.875rem' }}>Between</MenuItem>
                    </Select>
                  </FormControl>

                  <IconButton
                    color="error"
                    disabled={group.conditions.length <= 1}
                    onClick={() => removeCondition(groupIndex, conditionIndex)}
                    size="small"
                    aria-label="Remove condition"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>

                <TextField
                  label="Value"
                  value={condition.value}
                  onChange={e => handleConditionChange(groupIndex, conditionIndex, 'value', e.target.value)}
                  fullWidth
                  size="small"
                  placeholder={condition.operator === 'between' ? 'min, max' : 'Enter value'}
                  InputLabelProps={{ shrink: true }}
                  sx={{ mt: 1 }}
                />
              </Box>
            ))}
          </Stack>

          <Button
            onClick={() => addCondition(groupIndex)}
            startIcon={<AddIcon />}
            size="small"
            sx={{ mt: 1.5, textTransform: 'none' }}
          >
            Add Condition
          </Button>
        </Paper>
      ))}

      <Button
        onClick={addConditionGroup}
        variant="outlined"
        color="warning"
        startIcon={<AddIcon />}
        size="small"
        sx={{ textTransform: 'none', mt: 0.5 }}
      >
        Add OR Condition Group
      </Button>
    </>
  );
};

export default ConditionGroupsEditor;
